import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface AttractionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  linkUrl: string;
}

const AttractionModal: React.FC<AttractionModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  imageSrc,
  imageAlt,
  linkUrl,
}) => {
  const { t } = useTranslation();

  // Prevent background scrolling and handle ESC key when modal is open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-surface rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[90vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/20 hover:bg-black/40 text-white rounded-full flex items-center justify-center transition-colors shadow-sm"
          aria-label="Close modal"
        >
          <span className="material-symbols-rounded">close</span>
        </button>

        {/* Image Section */}
        <div className="w-full h-64 sm:h-80 relative shrink-0">
          <img 
            src={imageSrc} 
            alt={imageAlt} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        </div>

        {/* Text and Actions Section */}
        <div className="p-6 md:p-8 overflow-y-auto">
          <h3 className="font-headline text-3xl text-primary mb-4">{title}</h3>
          <p className="text-on-surface-variant text-base leading-relaxed mb-8">
            {description}
          </p>
          
          <div className="flex justify-end pt-4 border-t border-outline-variant/30 mt-auto">
            <a 
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-label uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg"
            >
              {t('aboutUsPage.attractions.readMore', 'Read more')}
              <span className="material-symbols-rounded text-sm">arrow_forward</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttractionModal;
